import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { CTA } from "../components/CTA";
import { CTASection } from "../components/CTASection";
import { getIndustryBySlug, INDUSTRIES } from "../lib/industries";
import {
  CERTIFICATION_LEVELS,
  CERTIFICATION_PRICES,
  RENEWAL_PRICES,
  levelColor,
  type CertificationLevel,
  type CertificationProgram,
} from "../lib/certifications";
import { useIdentity } from "../lib/identity";
import { createCheckoutSession } from "../lib/stripe-api";
import { fetchPrograms } from "../lib/sentinel-api";
import { CheckCircle2, AlertCircle, Building2, GraduationCap, Layers, ShieldCheck } from "../components/IndustryIcons";

const LEVEL_DESCRIPTIONS: Record<CertificationLevel, string> = {
  Professional: "Entry-level certification for individual operators and practitioners. Covers core safety monitoring fundamentals, sensor data interpretation, and basic intervention response.",
  Supervisor: "Mid-level certification for team leads and shift supervisors. Adds multi-operator coordination, incident triage, and cross-domain intervention template management.",
  Manager: "Advanced certification for department managers and safety directors. Includes organizational safety architecture, compliance management, and multi-team coordination.",
  Executive: "Top-level certification for senior leadership and C-suite. Covers strategic safety governance, enterprise-wide risk management, and multi-jurisdictional oversight.",
};

const LEVEL_RENEWAL_NOTES: Record<CertificationLevel, string> = {
  Professional: "Renewal requires completion of 8 hours of continuing education modules and a recertification quiz. Annual renewal keeps your credential active.",
  Supervisor: "Renewal requires 12 hours of continuing education, a recertification quiz, and documented supervision of at least 2 intervention exercises. Annual renewal.",
  Manager: "Renewal requires 16 hours of continuing education, a recertification exam, and submission of an organizational safety review. Annual renewal.",
  Executive: "Renewal requires 20 hours of continuing education, a recertification exam, and a strategic safety governance review. Annual renewal.",
};

interface ProfessionalPathway {
  name: string;
  icon: typeof GraduationCap;
  description: string;
  required: string;
  recommended: string;
  why: string;
}

const PROFESSIONAL_PATHWAYS: ProfessionalPathway[] = [
  {
    name: "Sentinel Consultant Certification",
    icon: GraduationCap,
    description: "Consultants apply Sentinel across diverse client environments, advising organizations on implementation, intervention design, and safety architecture best practices.",
    required: "Sentinel Core Certification",
    recommended: "At least one Industry Certification, plus organizational experience",
    why: "Consultants apply Sentinel across environments, so industry familiarity helps.",
  },
  {
    name: "Sentinel Architect Certification",
    icon: Layers,
    description: "Architects design and build complete Sentinel systems — sensor networks, diagnostic loops, intervention templates, and verification pipelines tailored to an organization's needs.",
    required: "Sentinel Core Certification",
    recommended: "At least one Industry Certification, engineering or systems background, and optionally Sentinel Engineering & Architectural Systems Design",
    why: "Architects build Sentinel systems — they need the fundamentals and technical context.",
  },
  {
    name: "Sentinel Auditor Certification",
    icon: ShieldCheck,
    description: "Auditors verify Sentinel compliance within organizations, conducting reviews of intervention logs, verification records, template usage, and reporting accuracy.",
    required: "Sentinel Core Certification",
    recommended: "At least one Industry Certification, plus compliance or QA background",
    why: "Auditors verify Sentinel compliance, so industry context helps.",
  },
  {
    name: "Sentinel Instructor Certification",
    icon: GraduationCap,
    description: "Instructors teach Sentinel curriculum to individuals and organizations, delivering Core, Industry, and Professional-level training with hands-on exercises and capstone simulations.",
    required: "Sentinel Core Certification",
    recommended: "At least one Industry Certification, plus teaching or training experience",
    why: "Instructors teach Sentinel — they need both fundamentals and applied context.",
  },
  {
    name: "Sentinel Engineering & Architectural Systems Design Certification",
    icon: Layers,
    description: "The deep systems design track. Covers sensor engineering, diagnostic loop architecture, intervention template engineering, and full-stack Sentinel system design for complex environments.",
    required: "Sentinel Core Certification",
    recommended: "Engineering background, and optionally Sentinel Architect Certification for synergy",
    why: "This is the deep systems design track — engineering fundamentals matter.",
  },
];

const PREREQUISITE_SUMMARY = [
  { cert: "Core", required: "None", recommended: "None" },
  { cert: "Industry Certifications", required: "Core", recommended: "None" },
  { cert: "Consultant", required: "Core", recommended: "Industry" },
  { cert: "Architect", required: "Core", recommended: "Industry + Engineering" },
  { cert: "Auditor", required: "Core", recommended: "Industry + Compliance" },
  { cert: "Instructor", required: "Core", recommended: "Industry + Teaching" },
  { cert: "Engineering & Architectural Systems Design", required: "Core", recommended: "Engineering + Architect" },
  { cert: "Organizational", required: "Core + Industry + Auditor + Consultant/Architect", recommended: "Full enterprise adoption" },
];

const ORG_REQUIREMENTS_INDIVIDUAL = [
  { role: "Required Staff", cert: "Core Certification", desc: "All staff who interact with the Sentinel platform must hold a current Core Certification." },
  { role: "Relevant Departments", cert: "Industry Certification", desc: "Department teams must hold the Industry Certification relevant to their operational domain." },
  { role: "Internal Compliance Staff", cert: "Auditor Certification", desc: "At least one internal compliance team member must hold a Sentinel Auditor Certification." },
  { role: "Implementation Leads", cert: "Consultant or Architect Certification", desc: "At least one implementation lead must hold a Consultant or Architect Certification." },
];

const ORG_REQUIREMENTS_SENTINEL = [
  "Annual audit by Sentinel",
  "Compliance verification",
  "Template usage verification",
  "Reporting verification",
  "Intervention/verification loop compliance",
];

interface LicensePlan {
  id: string;
  name: string;
  seats: number;
  price: number;
  priceId: string;
  type: "agency" | "enterprise";
  description: string;
  features: string[];
  popular?: boolean;
}

const LICENSE_PLANS: LicensePlan[] = [
  {
    id: "agency-5",
    name: "Agency Plan — 5 Seats",
    seats: 5,
    price: 2475,
    priceId: "price_1U03tMD2TH4f4lLQH0U6Y7qD",
    type: "agency",
    description: "Perfect for small teams and field offices",
    features: ["5 seats across all levels", "All 4 certification levels", "Annual renewal included", "Seat management dashboard", "Progress tracking"],
  },
  {
    id: "agency-10",
    name: "Agency Plan — 10 Seats",
    seats: 10,
    price: 4455,
    priceId: "price_1U03tMD2TH4f4lLQA3lawxI8",
    type: "agency",
    description: "Great for growing departments",
    features: ["10 seats across all levels", "All 4 certification levels", "Annual renewal included", "Seat management dashboard", "Progress tracking", "10% savings vs individual"],
    popular: true,
  },
  {
    id: "agency-25",
    name: "Agency Plan — 25 Seats",
    seats: 25,
    price: 8950,
    priceId: "price_1U03tMD2TH4f4lLQxhGGPV5g",
    type: "agency",
    description: "For mid-size agencies and precincts",
    features: ["25 seats across all levels", "All 4 certification levels", "Annual renewal included", "Seat management dashboard", "Progress tracking", "Bulk analytics reporting", "20% savings vs individual"],
  },
  {
    id: "enterprise-50",
    name: "Enterprise Plan — 50 Seats",
    seats: 50,
    price: 24950,
    priceId: "price_1U03tND2TH4f4lLQQtLDyIP8",
    type: "enterprise",
    description: "For large organizations and multi-site deployments",
    features: ["50 seats across all levels", "All 4 certification levels", "Annual renewal included", "Advanced seat management", "Org-wide analytics", "Custom branding", "Priority support", "Dedicated account manager"],
  },
];

const RENEWAL_FAQ = [
  { q: "When do renewals start?", a: "Your annual renewal begins 1 year from your certification date. You'll receive email reminders 30 days, 15 days, and 1 day before expiration." },
  { q: "What if my certification lapses?", a: "You have a 90-day grace period to reinstate by paying the renewal fee plus a reinstatement charge. After 90 days, you must retake the full certification program." },
  { q: "Do multi-seat plans include renewals?", a: "Yes. All agency and enterprise plans include annual renewal for every seat. Renewals are automatic as long as your plan is active." },
  { q: "Can I renew at a different level?", a: "Yes. You can upgrade to a higher certification level during renewal by paying the price difference plus the renewal fee for the new level." },
];

function PrerequisiteBadge({ label, type }: { label: string; type: "required" | "recommended" }) {
  const isRequired = type === "required";
  return (
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.375rem",
      padding: "0.3rem 0.625rem",
      borderRadius: "var(--radius-full)",
      fontSize: "0.75rem",
      fontWeight: 600,
      background: isRequired ? "rgba(220,38,38,0.08)" : "rgba(61,220,151,0.08)",
      color: isRequired ? "var(--error-700, #b91c1c)" : "var(--accent-700, #047857)",
      border: `1px solid ${isRequired ? "rgba(220,38,38,0.2)" : "rgba(61,220,151,0.2)"}`,
    }}>
      {isRequired ? <AlertCircle size={12} /> : <CheckCircle2 size={12} />}
      <span>{label}</span>
    </div>
  );
}

export default function IndustryCertificationPage() {
  const { industrySlug } = useParams<{ industrySlug: string }>();
  const { identity } = useIdentity();
  const industry = getIndustryBySlug(industrySlug ?? "");
  const [programs, setPrograms] = useState<CertificationProgram[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    fetchPrograms().then((p) => {
      setPrograms(p);
      setLoadingPrograms(false);
    });
  }, []);

  const industryPrograms = industry
    ? programs.filter(
        (p) => p.industry.toLowerCase() === industry.name.toLowerCase()
      )
    : [];

  async function handleIndividualCheckout(program: CertificationProgram) {
    if (!identity.isAuthenticated) {
      window.location.href = `/login?redirect=certifications/${industrySlug}`;
      return;
    }
    if (!identity.userId) {
      setCheckoutError("Unable to identify your account. Please log in again.");
      return;
    }
    setCheckoutLoading(program.id);
    setCheckoutError(null);
    const session = await createCheckoutSession({
      user_id: identity.userId,
      program_id: program.id,
      license_type: "individual",
      price_plan_id: program.price_plan_id,
      success_url: `${window.location.origin}/dashboard?checkout=success&program=${program.id}`,
      cancel_url: `${window.location.origin}/certifications/${industrySlug}?checkout=cancelled`,
    });
    setCheckoutLoading(null);
    if (session?.url) {
      window.location.href = session.url;
    } else {
      setCheckoutError("Unable to start checkout. Please try again or contact support.");
    }
  }

  async function handleLicenseCheckout(planId: string, priceId: string, licenseType: "agency" | "enterprise") {
    if (!identity.isAuthenticated) {
      window.location.href = `/login?redirect=certifications/${industrySlug}`;
      return;
    }
    if (!identity.userId) {
      setCheckoutError("Unable to identify your account. Please log in again.");
      return;
    }
    setCheckoutLoading(planId);
    setCheckoutError(null);
    const session = await createCheckoutSession({
      user_id: identity.userId,
      license_type: licenseType,
      price_plan_id: priceId,
      success_url: `${window.location.origin}/dashboard?checkout=success&plan=${planId}`,
      cancel_url: `${window.location.origin}/certifications/${industrySlug}?checkout=cancelled`,
    });
    setCheckoutLoading(null);
    if (session?.url) {
      window.location.href = session.url;
    } else {
      setCheckoutError("Unable to start checkout. Please try again or contact support.");
    }
  }

  if (!industry) {
    return (
      <>
        <SEO title="Industry Not Found — Sentinel" description="The industry you are looking for does not exist." />
        <Section>
          <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
            <h1>Industry Not Found</h1>
            <p className="text-muted" style={{ marginBottom: "2rem" }}>
              The industry you're looking for doesn't exist or has been moved.
            </p>
            <CTA to="/certification" variant="primary">Browse All Certifications</CTA>
          </div>
        </Section>
      </>
    );
  }

  const relatedIndustries = INDUSTRIES.filter(
    (i) => i.category === industry.category && i.slug !== industry.slug
  ).slice(0, 4);

  return (
    <>
      <SEO
        title={`${industry.name} Certifications — Sentinel`}
        description={`Certification programs for ${industry.name}. Core Certification, Industry Certification, Professional Pathways, and Organizational Certification with clear prerequisites.`}
        canonical={`/certifications/${industry.slug}`}
      />
      <PageHero
        eyebrow="Industry Certifications"
        title={`${industry.name} Certifications`}
        description={industry.tagline}
        bgVariant="steel"
        bgImage={industry.backgroundImage}
      />

      {/* Certification pathway overview */}
      <Section>
        <SectionHeader
          eyebrow="Certification Pathway"
          title="The Four-Tier Sentinel Certification Ecosystem"
          description={`Every Sentinel certification for ${industry.name} fits into a four-tier pathway. You must start with Core, then earn your Industry Certification, then optionally pursue Professional Pathways — and finally, your organization can earn Sentinel Organizational Certification.`}
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem" }}>
          {[
            { tier: 1, name: "Core Certification", icon: ShieldCheck, prereq: "No prerequisites", desc: "The foundation. Everyone must complete Core before any other Sentinel certification.", color: "var(--accent-600)" },
            { tier: 2, name: "Industry Certification", icon: Building2, prereq: "Prerequisite: Core", desc: `Applies Sentinel to ${industry.name} environments. Covers industry-specific safety challenges and intervention templates.`, color: "var(--primary-600)" },
            { tier: 3, name: "Professional Pathways", icon: GraduationCap, prereq: "Prerequisite: Core (Industry recommended)", desc: "Five specialized tracks: Consultant, Architect, Auditor, Instructor, and Engineering & Architectural Systems Design.", color: "var(--secondary-600)" },
            { tier: 4, name: "Organizational Certification", icon: Building2, prereq: "Prerequisite: Multiple individual certifications", desc: "Awarded to the company, not the person. Proves the entire organization operates under Sentinel architecture.", color: "var(--warning-600, #d97706)" },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.tier} className="card card-hover" style={{ padding: "1.5rem", borderTop: `3px solid ${t.color}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
                  <div style={{
                    width: "2.5rem", height: "2.5rem", borderRadius: "var(--radius-md)",
                    background: `${t.color}12`, display: "flex", alignItems: "center", justifyContent: "center",
                    color: t.color,
                  }}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)" }}>Tier {t.tier}</div>
                    <h3 style={{ fontSize: "1rem", marginBottom: 0 }}>{t.name}</h3>
                  </div>
                </div>
                <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5, marginBottom: "0.75rem" }}>{t.desc}</p>
                <div style={{ paddingTop: "0.5rem", borderTop: "1px solid var(--border)" }}>
                  <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-muted)" }}>{t.prereq}</span>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Tier 1: Core Certification */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow="Tier 1 — Foundation"
          title="Sentinel Core Certification"
          description="The foundation of the entire Sentinel certification ecosystem. Everyone must complete Core before taking any other Sentinel certification — no exceptions."
        />

        <div className="card" style={{ padding: "2rem", borderLeft: "4px solid var(--accent-600)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem", alignItems: "start" }}>
            <div>
              <h3 style={{ fontSize: "1.125rem", marginBottom: "0.75rem", color: "var(--accent-700, #047857)" }}>What Core Covers</h3>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {[
                  "Sentinel architecture and system design",
                  "The diagnostic loop: detect, diagnose, intervene, verify",
                  "Intervention logic and template usage",
                  "Verification logic and compliance checking",
                  "Platform navigation and dashboard operations",
                  "Safety domain fundamentals across all 12 domains",
                ].map((item) => (
                  <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.875rem" }}>
                    <CheckCircle2 size={16} style={{ color: "var(--accent-600)", flexShrink: 0, marginTop: "0.125rem" }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 style={{ fontSize: "1.125rem", marginBottom: "0.75rem" }}>Prerequisites</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: "0.375rem" }}>Required</div>
                  <PrerequisiteBadge label="None — Core is the starting point" type="required" />
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: "0.375rem" }}>Recommended</div>
                  <PrerequisiteBadge label="None" type="recommended" />
                </div>
              </div>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.6, marginTop: "1rem" }}>
                Core teaches the Sentinel architecture, diagnostic loop, intervention logic, verification logic, and template usage — the fundamentals required for every other track.
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", paddingTop: "1.5rem", borderTop: "1px solid var(--border)" }}>
            {identity.isAuthenticated ? (
              <CTA to="/certifications" variant="primary">Start Core Certification</CTA>
            ) : (
              <>
                <CTA to="/register" variant="primary">Register to Start</CTA>
                <CTA to="/login" variant="secondary">Log In</CTA>
              </>
            )}
          </div>
        </div>
      </Section>

      {/* Tier 2: Industry Certification — Four Levels */}
      <Section>
        <SectionHeader
          eyebrow={`Tier 2 — Industry Certification for ${industry.name}`}
          title={`Four Certification Levels for ${industry.name}`}
          description={`Once you hold a Core Certification, you can pursue your ${industry.name} Industry Certification at four progressive levels — from individual operator to enterprise-wide safety governance.`}
        />

        <div style={{ marginBottom: "1.5rem", padding: "0.75rem 1rem", background: "rgba(220,38,38,0.06)", borderRadius: "var(--radius-md)", border: "1px solid rgba(220,38,38,0.15)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <AlertCircle size={16} style={{ color: "var(--error-700, #b91c1c)", flexShrink: 0 }} />
          <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--error-700, #b91c1c)" }}>
            Prerequisite: Sentinel Core Certification is required before enrolling in any Industry Certification level.
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.5rem" }}>
          {CERTIFICATION_LEVELS.map((level) => (
            <div
              key={level}
              className="card card-hover"
              style={{
                padding: "1.5rem",
                display: "flex",
                flexDirection: "column",
                borderTop: `3px solid ${levelColor(level)}`,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                <h3 style={{ fontSize: "1.125rem", marginBottom: 0, color: levelColor(level) }}>{level}</h3>
                <span
                  style={{
                    padding: "0.25rem 0.625rem",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    background: `${levelColor(level)}15`,
                    color: levelColor(level),
                  }}
                >
                  Level {CERTIFICATION_LEVELS.indexOf(level) + 1}
                </span>
              </div>
              <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "1rem", flex: 1, lineHeight: 1.5 }}>
                {LEVEL_DESCRIPTIONS[level]}
              </p>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.5rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border)" }}>
                <div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>${CERTIFICATION_PRICES[level]}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>per year</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--accent-600)" }}>${RENEWAL_PRICES[level]}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>annual renewal</div>
                </div>
              </div>

              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
                {identity.isAuthenticated ? (
                  <CTA to="/certifications" variant="primary" style={{ flex: 1, fontSize: "0.8125rem" }}>
                    Enroll Now
                  </CTA>
                ) : (
                  <>
                    <CTA to="/register" variant="primary" style={{ flex: 1, fontSize: "0.8125rem" }}>
                      Register
                    </CTA>
                    <CTA to="/login" variant="secondary" style={{ flex: 1, fontSize: "0.8125rem" }}>
                      Log In
                    </CTA>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Renewal requirements */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow="Renewal Requirements"
          title="Annual Recertification"
          description="All Sentinel certifications require annual renewal to ensure credential holders stay current with evolving safety standards, new intervention protocols, and regulatory changes."
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.5rem" }}>
          {CERTIFICATION_LEVELS.map((level) => (
            <div
              key={level}
              className="card"
              style={{
                padding: "1.25rem",
                borderLeft: `3px solid ${levelColor(level)}`,
              }}
            >
              <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.5rem", color: levelColor(level) }}>{level} Renewal</h4>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.6, marginBottom: "0.75rem" }}>
                {LEVEL_RENEWAL_NOTES[level]}
              </p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "0.5rem", borderTop: "1px solid var(--border)" }}>
                <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Renewal fee</span>
                <span style={{ fontSize: "1rem", fontWeight: 700, color: "var(--accent-600)" }}>${RENEWAL_PRICES[level]}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="card" style={{ padding: "1.5rem", marginTop: "1.5rem", background: "var(--accent-50)", borderColor: "var(--accent-200)" }}>
          <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.5rem" }}>What Happens If You Don't Renew?</h4>
          <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.6, margin: 0 }}>
            If your certification lapses, your credential becomes inactive and you'll lose access to the Sentinel platform dashboards and intervention tools. You can reinstate a lapsed certification within 90 days by completing the renewal requirements and paying a reinstatement fee equal to the renewal fee. After 90 days, you must retake the full certification program.
          </p>
        </div>
      </Section>

      {/* Pricing — Individual Certifications */}
      <Section>
        <SectionHeader
          eyebrow="Pricing — Individual"
          title={`Individual Certification Pricing for ${industry.name}`}
          description="Purchase individual access to any Sentinel certification program for this industry. Each purchase includes 1-year access to all modules, quizzes, exams, and the capstone simulation."
        />

        {checkoutError && (
          <div className="card" style={{ padding: "1rem 1.5rem", marginBottom: "1rem", borderColor: "var(--error-400)", background: "var(--error-50)", color: "var(--error-700)", fontWeight: 600, fontSize: "0.875rem" }}>
            {checkoutError}
          </div>
        )}

        {loadingPrograms ? (
          <p className="text-center text-muted">Loading programs...</p>
        ) : industryPrograms.length === 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.5rem" }}>
            {CERTIFICATION_LEVELS.map((level) => (
              <div key={level} className="card" style={{ padding: "1.5rem", display: "flex", flexDirection: "column", borderTop: `3px solid ${levelColor(level)}` }}>
                <h3 style={{ fontSize: "1.125rem", marginBottom: 0, color: levelColor(level) }}>{level}</h3>
                <div style={{ fontSize: "1.5rem", fontWeight: 800, marginTop: "0.75rem" }}>
                  ${CERTIFICATION_PRICES[level]}<span style={{ fontSize: "0.75rem", fontWeight: 400, color: "var(--text-muted)" }}> / year</span>
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>Renewal: ${RENEWAL_PRICES[level]}</div>
                {identity.isAuthenticated ? (
                  <CTA to="/certifications" variant="primary" style={{ marginTop: "1rem", width: "100%", fontSize: "0.8125rem" }}>Enroll Now</CTA>
                ) : (
                  <CTA to="/register" variant="primary" style={{ marginTop: "1rem", width: "100%", fontSize: "0.8125rem" }}>Register to Purchase</CTA>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1rem" }}>
            {industryPrograms.map((program) => (
              <div key={program.id} className="card card-hover" style={{ padding: "1.25rem", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                  <div>
                    <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.25rem" }}>
                      {program.industry}
                    </div>
                    <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>{program.level}</h3>
                  </div>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.6875rem", color: "var(--text-muted)", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-sm)", background: "var(--bg-subtle)" }}>
                    {program.code}
                  </span>
                </div>
                <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.75rem", flex: 1 }}>{program.description}</p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>
                    ${program.price}<span style={{ fontSize: "0.75rem", fontWeight: 400, color: "var(--text-muted)" }}> / year</span>
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Renewal: ${program.renewal_price}</div>
                </div>
                <button
                  onClick={() => handleIndividualCheckout(program)}
                  disabled={checkoutLoading === program.id}
                  className="btn btn-primary"
                  style={{ width: "100%", fontSize: "0.8125rem" }}
                >
                  {checkoutLoading === program.id ? "Redirecting..." : identity.isAuthenticated ? "Purchase & Enroll" : "Log In to Purchase"}
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Pricing — Multi-Seat License Plans */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow="Pricing — Multi-Seat Plans"
          title="Team & Enterprise Pricing"
          description="Purchase seats in bulk and assign them to team members. All plans include access to all 4 certification levels across all industries."
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
          {LICENSE_PLANS.map((plan) => (
            <div
              key={plan.id}
              className="card card-hover"
              style={{
                padding: "1.5rem",
                display: "flex",
                flexDirection: "column",
                borderColor: plan.popular ? "var(--accent-400)" : "var(--border)",
                position: "relative",
              }}
            >
              {plan.popular && (
                <span className="badge badge-accent" style={{ position: "absolute", top: "0.75rem", right: "0.75rem" }}>
                  Most Popular
                </span>
              )}
              <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--accent-600)", marginBottom: "0.5rem" }}>
                {plan.type === "enterprise" ? "Enterprise" : "Agency"}
              </div>
              <h3 style={{ fontSize: "1.125rem", marginBottom: "0.5rem" }}>{plan.name}</h3>
              <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>{plan.description}</p>
              <div style={{ fontSize: "1.75rem", fontWeight: 800, marginBottom: "0.75rem" }}>
                ${plan.price.toLocaleString()}<span style={{ fontSize: "0.75rem", fontWeight: 400, color: "var(--text-muted)" }}> / year</span>
              </div>
              <ul style={{ display: "flex", flexDirection: "column", gap: "0.375rem", marginBottom: "1rem", flex: 1 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ display: "flex", gap: "0.5rem", fontSize: "0.8125rem" }}>
                    <span style={{ color: "var(--accent-500)", flexShrink: 0 }}>{"\u2713"}</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleLicenseCheckout(plan.id, plan.priceId, plan.type)}
                disabled={checkoutLoading === plan.id}
                className={plan.popular ? "btn btn-primary" : "btn btn-secondary"}
                style={{ width: "100%", fontSize: "0.8125rem" }}
              >
                {checkoutLoading === plan.id ? "Redirecting..." : identity.isAuthenticated ? `Purchase ${plan.name}` : "Log In to Purchase"}
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* Renewal FAQ */}
      <Section>
        <SectionHeader
          eyebrow="Renewal Policy"
          title="How Certification Renewals Work"
          description="All Sentinel certifications require annual renewal to keep your credentials active and ensure you stay current with evolving safety standards."
        />
        <div className="card" style={{ padding: "1.5rem", background: "var(--accent-50)", borderColor: "var(--accent-200)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1rem" }}>
            {RENEWAL_FAQ.map((item) => (
              <div key={item.q}>
                <div style={{ fontSize: "0.8125rem", fontWeight: 600, marginBottom: "0.25rem" }}>{item.q}</div>
                <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Tier 3: Professional Pathways */}
      <Section>
        <SectionHeader
          eyebrow="Tier 3 — Professional Pathways"
          title="Five Specialized Sentinel Career Tracks"
          description="After Core (and ideally an Industry Certification), you can pursue five professional-level certifications. These are not industry-specific — they demonstrate deep Sentinel expertise applicable across all environments."
        />

        <div style={{ marginBottom: "1.5rem", padding: "0.75rem 1rem", background: "rgba(220,38,38,0.06)", borderRadius: "var(--radius-md)", border: "1px solid rgba(220,38,38,0.15)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <AlertCircle size={16} style={{ color: "var(--error-700, #b91c1c)", flexShrink: 0 }} />
          <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--error-700, #b91c1c)" }}>
            Required: Sentinel Core Certification. Recommended: At least one Industry Certification.
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "1.5rem" }}>
          {PROFESSIONAL_PATHWAYS.map((pw) => {
            const Icon = pw.icon;
            return (
              <div key={pw.name} className="card card-hover" style={{ padding: "1.5rem", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
                  <div style={{
                    width: "2.5rem", height: "2.5rem", borderRadius: "var(--radius-md)",
                    background: "var(--secondary-50, #f0f9ff)", border: "1px solid var(--secondary-200, #bae6fd)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--secondary-600, #0284c7)",
                  }}>
                    <Icon size={20} />
                  </div>
                  <h3 style={{ fontSize: "1rem", marginBottom: 0 }}>{pw.name}</h3>
                </div>
                <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5, marginBottom: "1rem", flex: 1 }}>
                  {pw.description}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border)" }}>
                  <div>
                    <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: "0.25rem" }}>Required</div>
                    <PrerequisiteBadge label={pw.required} type="required" />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: "0.25rem" }}>Recommended</div>
                    <PrerequisiteBadge label={pw.recommended} type="recommended" />
                  </div>
                </div>
                <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", fontStyle: "italic", marginTop: "0.75rem", lineHeight: 1.5 }}>
                  {pw.why}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Prerequisite Summary Table */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow="Quick Reference"
          title="Prerequisite Map"
          description="A complete summary of what's required and recommended for every Sentinel certification."
        />

        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
              <thead>
                <tr style={{ background: "var(--bg-subtle, #f8fafc)" }}>
                  <th style={{ textAlign: "left", padding: "0.875rem 1rem", fontWeight: 700, borderBottom: "2px solid var(--border)" }}>Certification</th>
                  <th style={{ textAlign: "left", padding: "0.875rem 1rem", fontWeight: 700, borderBottom: "2px solid var(--border)" }}>Required Prerequisite</th>
                  <th style={{ textAlign: "left", padding: "0.875rem 1rem", fontWeight: 700, borderBottom: "2px solid var(--border)" }}>Recommended</th>
                </tr>
              </thead>
              <tbody>
                {PREREQUISITE_SUMMARY.map((row, idx) => (
                  <tr key={row.cert} style={{ borderBottom: idx < PREREQUISITE_SUMMARY.length - 1 ? "1px solid var(--border)" : "none" }}>
                    <td style={{ padding: "0.75rem 1rem", fontWeight: 600 }}>{row.cert}</td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      {row.required === "None" ? (
                        <span style={{ color: "var(--text-muted)" }}>None</span>
                      ) : (
                        <PrerequisiteBadge label={row.required} type="required" />
                      )}
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      {row.recommended === "None" ? (
                        <span style={{ color: "var(--text-muted)" }}>None</span>
                      ) : (
                        <PrerequisiteBadge label={row.recommended} type="recommended" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* Tier 4: Organizational Certification */}
      <Section>
        <SectionHeader
          eyebrow="Tier 4 — Organizational"
          title="Sentinel Organizational Certification"
          description="The only Sentinel certification awarded to a company, not an individual. It proves the entire organization operates under the Sentinel architecture — from frontline staff to compliance and leadership."
        />

        <div style={{ marginBottom: "1.5rem", padding: "0.75rem 1rem", background: "rgba(220,38,38,0.06)", borderRadius: "var(--radius-md)", border: "1px solid rgba(220,38,38,0.15)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <AlertCircle size={16} style={{ color: "var(--error-700, #b91c1c)", flexShrink: 0 }} />
          <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--error-700, #b91c1c)" }}>
            Prerequisite: Multiple individual certifications across the organization (see below).
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
          <div>
            <h3 style={{ fontSize: "1rem", marginBottom: "1rem" }}>Individual Certification Requirements (Company-Wide)</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {ORG_REQUIREMENTS_INDIVIDUAL.map((req) => (
                <div key={req.role} className="card" style={{ padding: "1rem", borderLeft: "3px solid var(--primary-600)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.375rem" }}>
                    <span style={{ fontSize: "0.8125rem", fontWeight: 700 }}>{req.role}</span>
                    <PrerequisiteBadge label={req.cert} type="required" />
                  </div>
                  <p className="text-muted" style={{ fontSize: "0.75rem", lineHeight: 1.5, margin: 0 }}>{req.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 style={{ fontSize: "1rem", marginBottom: "1rem" }}>Sentinel-Side Requirements</h3>
            <div className="card" style={{ padding: "1.5rem" }}>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.625rem" }}>
                {ORG_REQUIREMENTS_SENTINEL.map((req) => (
                  <li key={req} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.875rem" }}>
                    <ShieldCheck size={16} style={{ color: "var(--accent-600)", flexShrink: 0, marginTop: "0.125rem" }} />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.6, marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid var(--border)" }}>
                Organizational Certification proves the entire company is operating under Sentinel architecture. Annual audits and compliance verification are required to maintain this credential.
              </p>
            </div>

            <div className="card" style={{ padding: "1.5rem", marginTop: "1rem", background: "var(--accent-50)", borderColor: "var(--accent-200)" }}>
              <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.5rem" }}>Organizational Certification Includes</h4>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                {[
                  "Annual Sentinel audit of the organization",
                  "Compliance verification across all departments",
                  "Template usage verification and reporting",
                  "Intervention/verification loop compliance check",
                  "Organizational certification badge for public display",
                ].map((item) => (
                  <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.8125rem" }}>
                    <CheckCircle2 size={14} style={{ color: "var(--accent-600)", flexShrink: 0, marginTop: "0.25rem" }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "2rem" }}>
          {identity.isAuthenticated ? (
            <CTA to="/certifications" variant="primary">Apply for Organizational Certification</CTA>
          ) : (
            <>
              <CTA to="/register" variant="primary">Register Your Organization</CTA>
              <CTA to="/contact" variant="secondary">Talk to Us</CTA>
            </>
          )}
        </div>
      </Section>

      {/* Industry-specific certifications */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow="Industry-Specific Credentials"
          title={`Specialized Credentials for ${industry.name}`}
          description="In addition to the four-tier ecosystem, this industry offers specialized credentials tailored to its unique safety challenges."
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
          {industry.certification.map((cert, idx) => (
            <div key={idx} className="card card-hover" style={{ padding: "1.25rem", display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
              <div style={{
                flexShrink: 0,
                width: "2.5rem", height: "2.5rem",
                borderRadius: "var(--radius-md)",
                background: "var(--accent-50)",
                border: "1px solid var(--accent-200)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.875rem", fontWeight: 700, color: "var(--accent-600)",
              }}>
                {idx + 1}
              </div>
              <div>
                <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.25rem" }}>{cert}</h4>
                <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>
                  {idx === 0
                    ? "Core operator certification covering fundamental Sentinel platform operations and safety monitoring."
                    : idx === 1
                    ? "Domain-specific credential demonstrating expertise in this industry's unique safety challenges."
                    : "Coordinator credential for managing multi-team safety operations and cross-domain interventions."}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Training programs */}
      <Section>
        <SectionHeader
          eyebrow="Training Programs"
          title={`Training for ${industry.name}`}
          description="Each certification level includes structured training modules, hands-on exercises, and capstone simulations specific to this industry."
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
          {industry.training.map((course, idx) => (
            <div key={idx} className="card" style={{ padding: "1.25rem" }}>
              <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.375rem" }}>{course.name}</h4>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>{course.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Related industries */}
      {relatedIndustries.length > 0 && (
        <Section variant="subtle">
          <SectionHeader
            eyebrow="Related Industries"
            title={`More in ${industry.category}`}
            description="Explore certification programs for related industries in the same category."
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.75rem" }}>
            {relatedIndustries.map((rel) => (
              <Link
                key={rel.slug}
                to={`/certifications/${rel.slug}`}
                className="card card-hover"
                style={{ padding: "1rem", display: "block", textDecoration: "none" }}
              >
                <div style={{ fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.25rem" }}>{rel.name}</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>{rel.tagline}</div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* CTA */}
      <CTASection
        title="Ready to Get Certified?"
        description={`Start with Core, then earn your ${industry.name} Industry Certification. Register your organization, or log in if you already have an account.`}
        primaryLabel={identity.isAuthenticated ? "Browse Catalog" : "Register Now"}
        primaryTo={identity.isAuthenticated ? "/certifications" : "/register"}
        secondaryLabel={identity.isAuthenticated ? "Browse Catalog" : "Log In"}
        secondaryTo={identity.isAuthenticated ? "/certifications" : "/login"}
      />
    </>
  );
}
